import { useContext } from "react";
import { useOutletContext } from "react-router";
import styled, { css } from "styled-components";
import { truncateWithEllipsis } from "../../services/Utils";
import { Footer } from "../WorkSpace/CanvasViewer/Canvas/CanvasGeneralStyles";
import {
  FEED_INSTAGRAM,
  FEED_INSTAGRAM_PORTRAIT,
  PANFLETO,
  CARTAZ_GRANDE,
  CARTAZ_MEDIO,
  CARTAZ_PEQUENO,
  STORIES,
} from "../WorkSpace/CanvasViewer/EncarteConstants";
import WorkspaceContext from "../WorkSpace/WorkspaceContext";
import BrandAddress from "./BrandAddress";
import BrandContacts from "./BrandContacts";
import ExpirationDate from "./ExpirationDate";
import PaymentMethodList from "./PaymentMethodsList";
import { hexToRgba, rgbaToHex } from "./PickerAndSelector";
import { Payment } from "@mercadopago/sdk-react";

const StoriesFooter = styled(Footer)`
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  ${({ styles }) => css`
    background-color: ${rgbaToHex(styles?.bgColor, 0.75)};
    background-image: none;
  `}
  width: 100%;
  border-radius: 0;
  font-weight: bold;

  ${({ footerStyle }) =>
    footerStyle != "FULL_DETAILS" &&
    css`
      height: min-content;
    `}
`;

// Helper for Stories format footer height
export function getStoriesFooterRowHeight(footerStyle, hasNotice = false) {
  const base = COMBINED_FOOTER_BASE[footerStyle];
  if (base)
    return (
      getStoriesFooterRowHeight(base) + (hasNotice ? STORIES_NOTICE.height : 0)
    );
  switch (footerStyle) {
    case "FULL_DETAILS":
      return 350;
    case "NOTICE_ONLY":
      return hasNotice ? STORIES_NOTICE.height : 0;
    case "SOCIAL_MEDIA_ONLY":
      return 170;
    case "ADDRESS_AND_DATE_ONLY":
      return 145;
    case "PAYMENT_METHODS_ONLY":
      return 170;
    case "BRAND_NAME_AND_SLOGAN_ONLY":
      return 130;
    case "DATE_ONLY":
      return 100;
    case "HIDDEN":
      return 0;
    default:
      return 170;
  }
}

export function getFeedFooterRowHeight(footerStyle, hasNotice = false) {
  const base = COMBINED_FOOTER_BASE[footerStyle];
  if (base)
    return getFeedFooterRowHeight(base) + (hasNotice ? FEED_NOTICE.height : 0);
  switch (footerStyle) {
    case "NOTICE_ONLY":
      return hasNotice ? FEED_NOTICE.height : 0;
    case "FULL_DETAILS":
      return 0;
    case "SOCIAL_MEDIA_ONLY":
      return 165;
    case "ADDRESS_AND_DATE_ONLY":
      return 135;
    case "PAYMENT_METHODS_ONLY":
      return 120;
    case "BRAND_NAME_AND_SLOGAN_ONLY":
      return 130;
    case "DATE_ONLY":
      return 100;
    case "HIDDEN":
      return 0;
    default:
      return 0;
  }
}

export function getPanfletoFooterRowHeight(
  footerStyle,
  canvasScale,
  hasNotice = false,
) {
  const base = COMBINED_FOOTER_BASE[footerStyle];
  if (base)
    return (
      getPanfletoFooterRowHeight(base) +
      (hasNotice ? PANFLETO_NOTICE.height : 0)
    );
  switch (footerStyle) {
    case "FULL_DETAILS":
      return 400;
    case "NOTICE_ONLY":
      return hasNotice ? PANFLETO_NOTICE.height : 0;
    case "SOCIAL_MEDIA_ONLY":
      return 250;
    case "ADDRESS_AND_DATE_ONLY":
      return 180;
    case "PAYMENT_METHODS_ONLY":
      return 200;
    case "BRAND_NAME_AND_SLOGAN_ONLY":
      return 200;
    case "DATE_ONLY":
      return 180;
    case "HIDDEN":
      return 0;
    default:
      return 170;
  }
}

const FEED_NOTICE = { height: 75, fontSize: 30 };
const STORIES_NOTICE = { height: 85, fontSize: 34 };
const PANFLETO_NOTICE = { height: 110, fontSize: 44 };

const getNoticeSize = (format) => {
  switch (format) {
    case PANFLETO:
      return PANFLETO_NOTICE;
    case STORIES:
      return STORIES_NOTICE;
    default:
      return FEED_NOTICE;
  }
};

// The notice is premium-only, so it is hidden for non-premium accounts.
export function useFooterNotice() {
  const { encarte } = useContext(WorkspaceContext);
  const { isAccountPremium } = useOutletContext();
  return isAccountPremium ? encarte?.notice?.trim() || "" : "";
}

const NoticeBarWrapper = styled.div`
  ${({ canvasScale, format, styles }) => {
    const { height, fontSize } = getNoticeSize(format);
    return css`
      height: ${height * canvasScale}px;
      font-size: ${fontSize * canvasScale}px;
      padding: 0 ${20 * canvasScale}px;
      background-color: ${rgbaToHex(styles?.bgColor, 0.5)};
      color: ${rgbaToHex(styles?.txtColor, 1)};
      border-radius: ${100 * canvasScale}px;
    `;
  }}
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex-shrink: 0;
  overflow: hidden;
  overflow-wrap: anywhere;
  text-align: center;
  font-weight: bold;
  line-height: 1.1;
  text-transform: uppercase;
`;

const NoticeBar = ({ notice, canvasScale, format, styles }) => (
  <NoticeBarWrapper canvasScale={canvasScale} format={format} styles={styles}>
    {notice}
  </NoticeBarWrapper>
);

const BrandNamePhraseWrapper = styled.div`
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: #0c0c0c93;
  color: #fff;
  ${({ canvasScale, userBrand }) => css`
    border-bottom-right-radius: ${50 * canvasScale}px;

    h2 {
      font-size: ${userBrand.name && userBrand.name.length > 20
        ? 35 * canvasScale
        : 45 * canvasScale}px;
      text-transform: capitalize;
    }
    p {
      font-size: ${userBrand.name && userBrand.name.length > 20
        ? 32 * canvasScale
        : 24 * canvasScale}px;
      margin-bottom: ${10 * canvasScale}px;
      font-weight: bold;
      margin-top: 0;
      font-style: italic;
      text-stroke: 1px black;
    }
  `}
`;

const BrandNameAdressContactsWrapper = styled.div`
  display: grid;
  grid-template-columns: 40% 60%;
  height: 100%;
`;

const AddressContactsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

// Combined styles show the notice on top of an existing footer style.
const COMBINED_FOOTER_BASE = {
  NOTICE_ADDRESS_AND_DATE: "ADDRESS_AND_DATE_ONLY",
  NOTICE_SOCIAL_MEDIA: "SOCIAL_MEDIA_ONLY",
};

const NoticeAndContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ContentSlot = styled.div`
  display: grid;
  flex: 1;
  min-height: 0;
`;

export default function FooterStyles(props) {
  const { styles } = useContext(WorkspaceContext);
  const notice = useFooterNotice();
  const baseStyle = COMBINED_FOOTER_BASE[styles.footerStyle];

  if (!baseStyle)
    return <FooterContent {...props} footerStyle={styles.footerStyle} />;

  const content = <FooterContent {...props} footerStyle={baseStyle} />;
  if (!notice) return content;
  return (
    <NoticeAndContentWrapper>
      <NoticeBar
        notice={notice}
        canvasScale={props.canvasScale}
        format={styles.format}
        styles={props.headerAndFooterStyles}
      />
      <ContentSlot>{content}</ContentSlot>
    </NoticeAndContentWrapper>
  );
}

function FooterContent({ headerAndFooterStyles, canvasScale, footerStyle }) {
  const { userBrand, encarte, model, styles } = useContext(WorkspaceContext);
  const notice = useFooterNotice();

  switch (footerStyle) {
    case "NOTICE_ONLY":
      if (!notice) return null;
      return (
        <NoticeBar
          notice={notice}
          canvasScale={canvasScale}
          format={styles.format}
          styles={headerAndFooterStyles}
        />
      );
    case "FULL_DETAILS":
      if (styles.format === FEED_INSTAGRAM) return null;
      return (
        <FullDetailsFooterStyle
          headerAndFooterStyles={headerAndFooterStyles}
          model={model}
          canvasScale={canvasScale}
          encarte={encarte}
          userBrand={userBrand}
          format={styles.format}
        />
      );
    case "HIDDEN":
      return null;
    case "ADDRESS_AND_DATE_ONLY":
      return (
        <AddressAndDateOnlyFooterStyle
          userBrand={userBrand}
          headerAndFooterStyles={headerAndFooterStyles}
          canvasScale={canvasScale}
          encarte={encarte}
          format={styles.format}
        />
      );
    case "SOCIAL_MEDIA_ONLY":
      return (
        <SocialMediaOnlyFooterStyle
          headerAndFooterStyles={headerAndFooterStyles}
          canvasScale={canvasScale}
          userBrand={userBrand}
          format={styles.format}
        />
      );
    case "PAYMENT_METHODS_ONLY":
      return (
        <PaymentmethodsOnlyFooterStyle
          canvasScale={canvasScale}
          userBrand={userBrand}
          headerAndFooterStyles={headerAndFooterStyles}
          format={styles.format}
        />
      );
    case "BRAND_NAME_AND_SLOGAN_ONLY":
      return (
        <BrandNameAndSloganOnlyFooterStyle
          canvasScale={canvasScale}
          userBrand={userBrand}
          format={styles.format}
        />
      );
    case "DATE_ONLY":
      return (
        <DateOnlyFooterStyle
          canvasScale={canvasScale}
          encarte={encarte}
          headerAndFooterStyles={headerAndFooterStyles}
          format={styles.format}
        />
      );
    default:
      return null;
  }
}

const PanfletoFullDetailsStyles = css`
  ${({ canvasScale }) => css`
    .expiration-date {
      font-size: ${38 * canvasScale}px;
      line-height: ${30 * canvasScale}px;
      padding: ${12 * canvasScale}px 0;
    }

    .brand-address p {
      font-size: ${34 * canvasScale}px;
      .icon {
        font-size: ${44 * canvasScale}px !important;
      }
    }

    .contacts-wrapper {
    margin-top: ${20 * canvasScale}px;
      gap: ${5 * canvasScale}px;
      span {
        font-size: ${32 * canvasScale}px;
        padding-left: ${35 * canvasScale}px;
      }

      .media-icon {
        transform: translateX(${30 * canvasScale}px);
      }
    }


    .brand-name-phrase {
    overflow: hidden;
      h2 {
        font-size: ${72 * canvasScale}px;
        }

        p {
        font-size: ${42 * canvasScale}px;
        }


  `}
`;

const FullDetailsFooterWrapper = styled.div`
  ${({ styles }) => css`
    background-color: ${rgbaToHex(styles?.bgColor, 1)};
    background-image: none;
  `}

  ${({ canvasScale, format }) => css`
    .payment-method {
      height: ${100 * canvasScale}px;
    }
    ${format === PANFLETO && PanfletoFullDetailsStyles}
    .contacts-wrapper {
      margin-top: ${42 * canvasScale}px;
    }
  `}
`;

const FullDetailsFooterStyle = ({
  headerAndFooterStyles,
  model,
  canvasScale,
  encarte,
  userBrand,
  format,
}) => {
  return (
    <FullDetailsFooterWrapper
      canvasScale={canvasScale}
      styles={headerAndFooterStyles}
      format={format}
    >
      {headerAndFooterStyles && (
        <StoriesFooter
          model={model}
          canvasScale={canvasScale}
          styles={headerAndFooterStyles}
          footerStyle={"FULL_DETAILS"}
        >
          <ExpirationDate
            endDate={encarte.endDate}
            startDate={encarte.startDate}
            styles={headerAndFooterStyles}
            canvasScale={canvasScale}
            className={"expiration-date"}
            scale={canvasScale}
          />
          <BrandNameAdressContactsWrapper>
            <BrandNamePhraseWrapper
              canvasScale={canvasScale}
              userBrand={userBrand}
              styles={headerAndFooterStyles}
              className="brand-name-phrase"
            >
              <h2>{(userBrand?.name || "").substring(0, 25)}</h2>
              <p>
                {userBrand?.phrase
                  ? `"${userBrand.phrase.substring(0, 40)}"`
                  : ""}
              </p>
            </BrandNamePhraseWrapper>
            <AddressContactsWrapper>
              <BrandAddress
                userBrand={userBrand}
                styles={headerAndFooterStyles}
                canvasScale={canvasScale}
                className={"brand-address"}
              />
              <BrandContacts
                userBrand={userBrand}
                canvasScale={canvasScale}
                styles={headerAndFooterStyles}
                className={"contacts-wrapper"}
              />
            </AddressContactsWrapper>
          </BrandNameAdressContactsWrapper>
          <PaymentMethodListWrapper
            canvasScale={canvasScale}
            styles={headerAndFooterStyles}
            format={format}
          >
            {userBrand?.paymentMethods &&
              userBrand.paymentMethods.length > 0 && (
                <PaymentMethodList
                  methods={userBrand.paymentMethods}
                  canvasScale={canvasScale}
                  className={"payment-methods"}
                />
              )}
          </PaymentMethodListWrapper>
        </StoriesFooter>
      )}
    </FullDetailsFooterWrapper>
  );
};

const FeedStoriesAddressAndDateOnlyStyles = css`
  ${({ canvasScale }) => css`
    .expiration-date {
      font-size: ${30 * canvasScale}px;
      line-height: ${30 * canvasScale}px;
      padding: ${8 * canvasScale}px 0;
    }

    .brand-address p {
      font-size: ${28 * canvasScale}px;
    }

    .brand-address {
      font-size: ${28 * canvasScale}px;
      .icon {
        font-size: ${36 * canvasScale}px !important;
      }
    }
  `}
`;

const PanfletoAddressAndDateOnlyStyles = css`
  ${({ canvasScale }) => css`
    .expiration-date {
      font-size: ${45 * canvasScale}px;
      line-height: ${45 * canvasScale}px;
      padding: ${12 * canvasScale}px 0;
    }

    .brand-address p {
      font-size: ${42 * canvasScale}px;
    }

    .brand-address {
      font-size: ${42 * canvasScale}px;
      .icon {
        font-size: ${44 * canvasScale}px !important;
      }
    }
  `}
`;

const AddressAndDateOnlyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${({ headerAndFooterStyles, format }) => css`
    background-color: ${rgbaToHex(headerAndFooterStyles?.txtColor, 1)};
    color: ${rgbaToHex(headerAndFooterStyles?.txtColor, 1)};
    .brand-address p {
      font-weight: bold;
      color: #ffffff;
    }

    ${format === FEED_INSTAGRAM ||
    format === STORIES ||
    format === FEED_INSTAGRAM_PORTRAIT
      ? FeedStoriesAddressAndDateOnlyStyles
      : PanfletoAddressAndDateOnlyStyles}
  `}
`;

export const AddressAndDateOnlyFooterStyle = ({
  userBrand,
  headerAndFooterStyles,
  canvasScale,
  encarte,
  format,
}) => {
  return (
    <AddressAndDateOnlyWrapper
      canvasScale={canvasScale}
      headerAndFooterStyles={headerAndFooterStyles}
      format={format}
    >
      <ExpirationDate
        endDate={encarte.endDate}
        startDate={encarte.startDate}
        styles={headerAndFooterStyles}
        scale={canvasScale}
        className="expiration-date"
      />
      <BrandAddress
        userBrand={userBrand}
        styles={headerAndFooterStyles}
        canvasScale={canvasScale}
        className="brand-address"
      ></BrandAddress>
    </AddressAndDateOnlyWrapper>
  );
};

const SocialMediaOnlyWrapper = styled.div`
  .social-media {
    width: 100%;
    align-items: center;
    justify-items: start;
    justify-content: center;
    align-content: center;
    ${({ brand, canvasScale, format }) => {
      const fontSize =
        format === PANFLETO ? 45 * canvasScale : 30 * canvasScale;
      const iconSize = format === PANFLETO ? 8 * canvasScale : 5 * canvasScale;
      console.log(format, iconSize, fontSize);
      return `
        padding: ${10 * canvasScale}px 0;
        transform: translateX(${-10 * canvasScale}px);
        

        .logo-social-wrapper {
            gap: 0;
            width: 100%;

            span {
                width: 100%;
                padding-left: ${25 * canvasScale}px;
            }

            .media-icon {
                width: ${iconSize}rem;
                height: ${iconSize}rem;
            }
        }
        span{
        
        font-size: ${fontSize}px;
        }
      grid-template-columns: repeat(1, 1fr);

      `;
    }}

    .delivery {
      display: none;
    }
  }
`;

export const SocialMediaOnlyFooterStyle = ({
  headerAndFooterStyles,
  canvasScale,
  userBrand,
  format,
}) => {
  return (
    <SocialMediaOnlyWrapper
      brand={userBrand}
      canvasScale={canvasScale}
      format={format}
    >
      <BrandContacts
        canvasScale={canvasScale}
        styles={headerAndFooterStyles}
        className={"social-media"}
        shouldLimitLength={false}
      ></BrandContacts>
    </SocialMediaOnlyWrapper>
  );
};

export const PaymentMethodListWrapper = styled.div`
  p {
    text-align: center;
    font-weight: bold;
  }
  .payment-methods {
    display: flex;
    gap: 0;
    justify-content: center;

    align-content: center;
    .payment-method {
      border-radius: 0;
    }
  }
  background-color: #ffffff;

  ${({ canvasScale, styles, format, pmSizeOverflow }) => {
    let pmSize = 60;
    let gridTemplateColumns = 10;
    let gap = 30;
    let padding = 20;
    let fontSize = 28;
    switch (format) {
      case CARTAZ_GRANDE:
      case PANFLETO:
        pmSize = 90;
        fontSize = 60;
        gap = 40;
        break;
      case STORIES:
        pmSize = 70;
        fontSize = 40;
        gap = 30;
        padding = 0;
        break;

      case FEED_INSTAGRAM:
      case FEED_INSTAGRAM_PORTRAIT:
        padding = 0;
    }

    return css`
      .payment-methods {
        ${gridTemplateColumns > 0 &&
        `display: grid; 
    grid-template-columns: repeat(${gridTemplateColumns}, min-content); 
    justify-items: center; justify-content: center;`}
        padding: ${padding * canvasScale}px 0;
        gap: ${gap * canvasScale}px;
        .payment-method {
          width: ${pmSize * canvasScale}px;
          height: auto;
          height: ${100 * canvasScale}px;
        }
      }
      p {
        font-size: ${fontSize * canvasScale}px;
        color: #1d1d1dff;
        text-transform: uppercase;
      }
    `;
  }}
`;

export const PaymentmethodsOnlyFooterStyle = ({
  canvasScale,
  userBrand,
  headerAndFooterStyles,
  format,
}) => {
  console.log(format);
  return (
    <PaymentMethodListWrapper
      canvasScale={canvasScale}
      styles={headerAndFooterStyles}
      format={format}
    >
      <p>Nossos métodos de pagamento aceitos</p>
      {userBrand?.paymentMethods && userBrand.paymentMethods.length > 0 && (
        <PaymentMethodList
          methods={userBrand.paymentMethods}
          canvasScale={canvasScale}
          className={"payment-methods"}
        />
      )}

      {(!userBrand?.paymentMethods ||
        userBrand.paymentMethods.length === 0) && (
        <p>NENHUM MÉTODO DE PAGAMENTO SELECIONADO</p>
      )}
    </PaymentMethodListWrapper>
  );
};

const FeedStoriesBrandNameAndSloganStyles = css`
  ${({ canvasScale }) => css`
    .name {
      font-size: ${55 * canvasScale}px;
    }

    .slogan {
      font-size: ${40 * canvasScale}px;

  `}
`;

const PanfletoBrandNameAndSloganStyles = css`
  ${({ canvasScale }) => css`
    .name {
      font-size: ${74 * canvasScale}px;
      line-height: .8 !important;
      margin-bottom: ${16 * canvasScale}px;
    }

    .slogan {
      font-size: ${55 * canvasScale}px;

  `}
`;

const BrandNameAndSloganWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: #0c0c0cbe;
  color: #fff;
  font-weight: bold;
  text-align: center;
  line-height: 1.2;

  .name {
    background-color: #0c0c0c52;
  }

  .slogan {
    font-style: italic;
  }

  ${({ format }) => {
    return format === FEED_INSTAGRAM ||
      format === STORIES ||
      format === FEED_INSTAGRAM_PORTRAIT
      ? FeedStoriesBrandNameAndSloganStyles
      : PanfletoBrandNameAndSloganStyles;
  }}
`;

export const BrandNameAndSloganOnlyFooterStyle = ({
  canvasScale,
  userBrand,
  format,
}) => {
  return (
    <BrandNameAndSloganWrapper
      canvasScale={canvasScale}
      userBrand={userBrand}
      format={format}
    >
      <p className="name">{truncateWithEllipsis(userBrand?.name, 32)}</p>
      <p className="slogan">"{truncateWithEllipsis(userBrand?.phrase, 45)}"</p>
    </BrandNameAndSloganWrapper>
  );
};

const FeedStoriesDateOnlyStyles = css`
  ${({ canvasScale }) => `
    .expiry-date {
      font-size: ${36 * canvasScale}px;
    }
    
    `}
`;

const PanfletoDateOnlyStyles = css`
  ${({ canvasScale }) => `
    .expiry-date {
      font-size: ${56 * canvasScale}px;
      line-height: 1 !important;
      }`}
`;
const DateOnlyWrapper = styled.div`
  ${({ format }) => {
    return format === FEED_INSTAGRAM ||
      format === STORIES ||
      format === FEED_INSTAGRAM_PORTRAIT
      ? FeedStoriesDateOnlyStyles
      : PanfletoDateOnlyStyles;
  }}
  display: flex;
  align-items: center;
  justify-content: center;
  .expiry-date {
    background-color: #0c0c0ca9;
    color: #fff;
    line-height: 1.3;
  }
`;
export const DateOnlyFooterStyle = ({
  encarte,
  headerAndFooterStyles,
  canvasScale,
  format,
}) => {
  return (
    <DateOnlyWrapper scale={canvasScale} format={format}>
      <ExpirationDate
        endDate={encarte.endDate}
        startDate={encarte.startDate}
        styles={headerAndFooterStyles}
        className={"expiry-date"}
      />
    </DateOnlyWrapper>
  );
};
